import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'
import { Caption, H6, Overline, Subtitle2 } from '@src/elements/typography/typography'
import { ExerciseResponse } from '@src/data/exercise-response'
import QrcodeVue from 'qrcode.vue'
import Exercise from '@src/data/exercise'
import { getExercisePreparationSteps } from '@src/util/functions'
import { exerciseName } from '@src/util/exercise-name'

import './exercise-card-print.sass'

// a picture that takes longer than this no longer holds the print back
export const PREVIEW_TIMEOUT = 10000

@Component
export class ExerciseCardPrint extends Vue {

  @Prop({ required: true })
  exercise: ExerciseResponse | Exercise

  @Prop({ required: false, default: false })
  print: boolean

  private gone = false

  created () {
    if (this.print) {
      // the browser prints what it has, so the picture has to be loaded first: the card shows the GIF from a copy of
      // its own, so it is not in the cache yet
      this.$nextTick(async () => {
        await this.previewLoaded()
        if (!this.gone) this.printExercise()
      })
    }
  }

  previewLoaded (): Promise<void> {
    const img = this.$el.querySelector('img.exercise-preview') as HTMLImageElement
    if (img.complete) return Promise.resolve()
    return new Promise(resolve => {
      let timer = 0
      const done = () => {
        window.clearTimeout(timer)
        img.removeEventListener('load', done)
        img.removeEventListener('error', done)
        resolve()
      }
      timer = window.setTimeout(done, PREVIEW_TIMEOUT)
      img.addEventListener('load', done)
      img.addEventListener('error', done)
    })
  }

  printFromIframes () {
    const allIframes = document.querySelectorAll('iframe')
    alert(allIframes)

    for (let i = 0; i < allIframes.length; i++) {
      let iframe = allIframes[i]
      // @ts-ignore
      iframe.sandbox += ' allow-scripts allow-modals'
    }
  }

  printExercise () {
    this.$el.classList.add('exercise-to-print')
    window.print()
  }

  beforeDestroy () {
    this.gone = true
    this.$el.classList.remove('exercise-to-print')
  }

  render (h: CreateElement) {
    // a card about to print stays hidden on screen while its picture loads
    return <div class={{ 'exercise-card-print': true, 'exercise-to-print': this.print }}>
      {this.renderExerciseNumber(h)}
      <div class='title-equipment'>
        {this.renderTitle(h)}
        {this.renderEquipment(h)}
      </div>
      <div class='exercise-content'>
        {this.renderPreview(h)}
        {this.renderExecutionDetails(h)}
        {this.renderQrCode(h)}
      </div>
    </div>
  }

  renderPreview (h: CreateElement) {
    let gifUrl = this.exercise.videos.filter((video) => video.content_type === 'image/gif').map((video) => video.url)
    return <div class='preview'>
      {gifUrl.length ? <img class='exercise-preview' src={gifUrl[0]} /> : <img class='exercise-preview' src='/assets/icon/brand/default-exercise.jpg' />}
    </div>
  }

  renderExecutionDetails (h: CreateElement) {
    const preparationSteps: string[] = []
    getExercisePreparationSteps(this, this.exercise.number, 'DEFAULT', preparationSteps)
    if (preparationSteps.length) {
      return <div class='execution-details'>
        {preparationSteps.map((key, index) => {
          return <div class='line'>
        <span class='index'><Subtitle2>{index + 1}</Subtitle2></span>
        <span class='description'><Subtitle2>{this.$t('EXERCISE[' + this.exercise.number + '].PREPARATION_INSTRUCTIONS.' + key + '.DEFAULT')}</Subtitle2></span>
        </div>
        })}
      </div>
    }
  }

  renderTitle (h: CreateElement) {
    return <H6 class='title'>
      { exerciseName(this, this.exercise) }
    </H6>
  }

  renderExerciseNumber (h: CreateElement) {
    return <Overline class='header'>{this.exercise.number}</Overline>
  }

  renderEquipment (h: CreateElement) {
    const eqNames = this.exercise instanceof ExerciseResponse ? this.exercise.equipment : this.exercise.equipment.map(eq => eq.name ? eq.name : eq)
    return <div class='specs'>
      {this.exercise.equipment.length > 0 ?
        <Caption><b>{this.$t('REQUIRED')}</b> | {eqNames.map(eq => this.$t(`PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.${eq.toString().toUpperCase()}`)).join(', ')}
        </Caption>
        : null}
    </div>
  }

  renderQrCode (h: CreateElement) {
    return <QrcodeVue value={`ex_${this.exercise.number}`} size={120} class='generated-qr-code' />
  }

}
